<template>
  <div ref="containerElement" class="w-full" style="text-align: left" />
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import * as monaco from 'monaco-editor'

import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useEditorPageStatePresenter } from '@/presentation/composables/editor/useEditorPageStatePresenter'

monaco.languages.register({
  id: 'haskell'
})
monaco.languages.register({
  id: 'd'
})
monaco.languages.register({
  id: 'scala'
})
monaco.languages.register({
  id: 'ocaml'
})

export default defineComponent({
  props: {
    value: {
      type: String,
      required: true
    },
    change: {
      type: Function,
      required: true
    },
    isEditable: {
      type: Boolean,
      required: true
    }
  },
  setup(props, ctx) {
    const containerElement = ref<HTMLElement | null>(null)

    let windowWidth = ref(window.innerWidth)
    let windowHeight = ref(window.innerHeight)

    let editor: monaco.editor.IStandaloneCodeEditor
    const { editorFontSize, viewMode } = useConfigStatePresenter()
    const { testFrameHeight, testPanelHeight } = useEditorPageStatePresenter()

    const initMonaco = () => {
      if (containerElement.value != null) {
        editor = monaco.editor.create(containerElement.value, {
          theme: viewMode.value == 'LIGHT' ? 'vs' : 'vs-dark',
          value: props.value,
          automaticLayout: true,
          scrollBeyondLastLine: false,
          readOnly: !props.isEditable,
          lineNumbers: 'off',
          lineNumbersMinChars: 0,
          lineDecorationsWidth: 0,
          folding: false,
          glyphMargin: false,
          minimap: { enabled: false }
        })

        setFontSize(editorFontSize.value)
        editorDidMount()
      }
    }

    const editorDidMount = () => {
      editor.onDidChangeModelContent((event: monaco.editor.IModelContentChangedEvent) => {
        const value = editor.getValue()
        props.change(value)
      })
      window.addEventListener('resize', updateLayout)
      ctx.emit('editorDidMount', editor)
    }

    const destroyMonaco = () => {
      if (typeof editor !== 'undefined') {
        window.removeEventListener('resize', updateLayout)
        editor.dispose()
      }
    }

    const setFontSize = (size: number) => {
      let options = { fontSize: size }
      editor.updateOptions(options)
    }

    const updateLayout = (event: any) => {
      windowWidth = ref(window.innerWidth)
      windowHeight = ref(window.innerHeight)
      setLayout()
    }

    const setLayout = () => {
      if (editor) {
        editor.getDomNode()!!.style.height = `${testPanelHeight.value - 21}px`
        editor.layout()
      }
    }

    watch(
      () => testFrameHeight.value,
      () => {
        setLayout()
      }
    )

    watch(
      () => viewMode.value,
      () => {
        destroyMonaco()
        initMonaco()
        setLayout()
      }
    )

    watch(
      () => editorFontSize.value,
      () => {
        setFontSize(editorFontSize.value)
      }
    )

    watch(
      () => props.value,
      newValue => {
        if (editor) {
          if (newValue !== editor.getValue()) {
            editor.setValue(newValue)
          }
        }
      }
    )

    onMounted(() => {
      initMonaco()
      setLayout()
    })

    onBeforeUnmount(() => {
      destroyMonaco()
    })

    return {
      containerElement
    }
  }
})
</script>
